import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { OrderingOperations, OrderingSelectors, OrderingUtils } from 'polygon-ordering';
import RedcatPaySavedCardPaymentForm from '../shared/redcatpayments/components/RedcatPaySavedCardPaymentForm';
import history from '../history';
import { PURCHASE_GIFT_CARD_ROUTE } from '../constants/loyaltyRoutes';
import { enqueueErrorSnackbar } from '../utils/snackbar';
import { getRedcatPaySaleFailureMessage } from '../utils/errors';
import { setCurrentModal } from '../thunks/setCurrentModal';
import { GIFTCARD_PURCHASE_SUCCESSFUL_MODAL } from '../modals/GiftcardSuccessModal';

const { getMember, getCalculatedGiftcardSurcharge } = OrderingSelectors;
const { dollarsToCents } = OrderingUtils;
const { finaliseLoyaltyOrder } = OrderingOperations;

interface GiftCardPurchaseFormValues {
  Amount: string;
  surcharge?: number;
  // non-member buyer fields
  FirstName?: string;
  LastName?: string;
  Email?: string;
  ConfirmEmail?: string;
  // recipient fields (for-others)
  RecipientName?: string;
  RecipientSurname?: string;
  RecipientEmail?: string;
  Memo?: string;
  SendTime?: string;
  timezone?: string;
  delivery?: boolean;
}

const GiftCardPurchaseRedcatPay = () => {
  const { t } = useTranslation();
  const { values, initialValues, resetForm, validateForm } =
    useFormikContext<GiftCardPurchaseFormValues>();
  const dispatch = useAppDispatch();
  const member = useAppSelector(getMember);
  const orderTotal = dollarsToCents(Number(values.Amount) || 0);
  const surcharge = useAppSelector(state =>
    getCalculatedGiftcardSurcharge(state, Number(values.Amount) || 0),
  );

  const surchargeAmount = surcharge?.value ?? 0;
  const amount = orderTotal + surchargeAmount;

  const buyerEmail = values.Email ?? member?.email ?? '';
  const buyerFirstName = values.FirstName ?? member?.givenName ?? '';
  const buyerLastName = values.LastName ?? member?.surname ?? '';

  const isGift = Boolean(values.RecipientEmail);
  const recipientEmail = isGift ? values.RecipientEmail! : buyerEmail;
  const recipientName = isGift ? values.RecipientName! : buyerFirstName;
  const recipientSurname = isGift ? values.RecipientSurname! : buyerLastName;
  const scheduled = isGift && Boolean(values.delivery) && Boolean(values.SendTime);

  const loyalty = {
    loyalty_purchase_type: 'giftcard_purchase',
    Email: buyerEmail,
    ConfirmEmail: values.ConfirmEmail ?? buyerEmail,
    GivenNames: buyerFirstName,
    Surname: buyerLastName,
    ...(isGift && {
      RecipientEmail: recipientEmail,
      RecipientName: recipientName,
      RecipientSurname: recipientSurname,
    }),
    Memo: values.Memo ?? '',
    SendTime: values.SendTime ?? null,
    OrderTotal: orderTotal,
    Surcharge: surchargeAmount > 0 ? { Amount: surchargeAmount, Type: 11 } : null,
  };

  const finalisePurchase = async (reference: string) => {
    try {
      await dispatch(finaliseLoyaltyOrder(reference)).unwrap();
      dispatch(
        setCurrentModal({
          modalId: GIFTCARD_PURCHASE_SUCCESSFUL_MODAL,
          params: {
            isGift,
            purchaserEmail: buyerEmail,
            recipientEmail,
            scheduled,
            sendTime: values.SendTime,
          },
        }),
      );
      resetForm();
      // resetForm() clears state.errors directly without re-running validation, so
      // isValid would otherwise stay true against the stale (just-paid) values until
      // the next change/blur/submit. validateForm() without args reads from a stale
      // closure over state.values because it's called synchronously before the
      // reset's state update lands, so we validate against initialValues (what the
      // form is actually reset to) explicitly instead.
      await validateForm(initialValues);
      history.push(PURCHASE_GIFT_CARD_ROUTE);
    } catch (err: any) {
      const failureMessage = getRedcatPaySaleFailureMessage(err, {
        refundFailedMessage: t('refundFailedMessage'),
        fallbackMessage: t('giftCardPurchaseFailedMessage'),
      });
      enqueueErrorSnackbar(failureMessage);
    }
  };

  return (
    <RedcatPaySavedCardPaymentForm
      amount={amount}
      loyalty={loyalty}
      receiptEmail={buyerEmail || undefined}
      buttonLabel={t('button.purchaseButton')}
      failureMessage={t('giftCardPurchaseFailedMessage')}
      goBackOnNewCardError
      onFinalise={finalisePurchase}
    />
  );
};

export default GiftCardPurchaseRedcatPay;
